import Shepherd from 'shepherd.js';
import { TOUR_BUTTONS } from '../shepherd-defaults';
import {
  dismissCurrentShepherdStep,
  ensureShepherdModalOverlayHidden,
  ensureShepherdModalOverlayVisibleForStep,
  registerInteractiveTourGuards,
  shouldSkipShepherdStepReshow,
  wasShepherdStepDismissedByUser,
} from '../shepherd-interactive-step';
import {
  registerTourHighlightedFieldLockGuards,
  releaseAllShepherdTourFieldLocks,
  SHEPHERD_TOUR_READONLY_HIGHLIGHT_STEP_OPTIONS,
  withTourFieldLockTipMessage,
} from '../shepherd-tour-field-lock';
import {
  clearTrainingTourUi,
  resetTrainingFiltersTargetPadding,
  resolveTrainingFiltersTarget,
  setTrainingFiltersDatepickerOpen,
  setTrainingFiltersInteractionActive,
  setTrainingTourSpotlightActive,
  TRAINING_FILTERS_STEP_ID,
} from '../training-filters.shepherd';
import { delay, waitForElement } from '../shepherd.utils';
import { TRAINING_BOOK_SESSION_TOUR_PENDING_KEY } from './training-book-session.tour';

export { TRAINING_FILTERS_STEP_ID } from '../training-filters.shepherd';

type ShepherdTour = InstanceType<typeof Shepherd.Tour>;

export const TRAINING_BOOK_TRAINER_TOUR = 'training-book-trainer';

export const TRAINING_RESULTS_STEP_ID = 'training-list-results-area';

export const TRAINING_LIST_BOOK_BTN_STEP_ID = 'training-list-book-btn';

const TRAINING_SPOTLIGHT_STEP_IDS = new Set([
  TRAINING_RESULTS_STEP_ID,
  TRAINING_LIST_BOOK_BTN_STEP_ID,
  'training-list-no-trainers',
]);

function trainingResultsPanelInDom(): boolean {
  return !!document.querySelector('[data-tour="training-results-panel"]');
}

/** Show results-area tip after filters OK when search results (trainers or empty) are already on screen. */
export function tryShowTrainingResultsStepIfReady(tour: ShepherdTour): void {
  if (!trainingResultsPanelInDom()) {
    return;
  }
  const current = tour.getCurrentStep?.() as { id?: string } | undefined;
  if (!current || current.id !== TRAINING_FILTERS_STEP_ID) {
    return;
  }
  const resultsStep = tour.getById?.(TRAINING_RESULTS_STEP_ID) as
    | { id?: string }
    | undefined;
  if (wasShepherdStepDismissedByUser(resultsStep)) {
    return;
  }
  void tour.show(TRAINING_RESULTS_STEP_ID);
}

function resolveTrainingBookSessionBtnTarget(): HTMLElement | null {
  const buttons = document.querySelectorAll('[data-tour="training-book-session-btn"]');
  for (let i = 0; i < buttons.length; i++) {
    const node = buttons[i];
    if (
      node instanceof HTMLButtonElement &&
      !node.disabled &&
      node.getClientRects().length > 0
    ) {
      return node;
    }
  }
  const first = buttons[0];
  return first instanceof HTMLElement ? first : null;
}

function scrollTrainingBookSessionBtnIntoView(): void {
  const target = resolveTrainingBookSessionBtnTarget();
  if (!target) {
    return;
  }
  target.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
}

/** After results-step OK — scroll to Book Training Session and show that tip. */
export function tryShowTrainingBookBtnStepIfReady(tour: ShepherdTour): void {
  if (!document.querySelector('[data-tour="training-book-session-btn"]')) {
    return;
  }
  const current = tour.getCurrentStep?.() as { id?: string } | undefined;
  if (!current || current.id !== TRAINING_RESULTS_STEP_ID) {
    return;
  }
  const bookStep = tour.getById?.(TRAINING_LIST_BOOK_BTN_STEP_ID) as { id?: string } | undefined;
  if (wasShepherdStepDismissedByUser(bookStep)) {
    return;
  }
  if (shouldSkipShepherdStepReshow(tour, TRAINING_LIST_BOOK_BTN_STEP_ID)) {
    return;
  }
  scrollTrainingBookSessionBtnIntoView();
  void tour.show(TRAINING_LIST_BOOK_BTN_STEP_ID);
}

const TRAINING_RESULTS_STEP_OK = {
  text: 'OK',
  classes: 'shepherd-button-primary',
  action(this: ShepherdTour) {
    if (document.querySelector('[data-tour="training-book-session-btn"]')) {
      dismissCurrentShepherdStep(this);
      const schedule = (): void => tryShowTrainingBookBtnStepIfReady(this);
      queueMicrotask(schedule);
      requestAnimationFrame(schedule);
      void delay(400).then(schedule);
      return;
    }
    void this.next();
  },
};

const listStep2Ok = {
  text: 'OK',
  classes: 'shepherd-button-primary',
  action(this: ShepherdTour) {
    try {
      sessionStorage.setItem(TRAINING_BOOK_SESSION_TOUR_PENDING_KEY, '1');
    } catch {
      // ignore quota / private mode
    }
    this.complete();
  },
};

/** Remove filters-step dim/lock when user taps OK (tip only — tour continues). */
function clearTrainingFiltersStepOverlay(): void {
  setTrainingFiltersInteractionActive(false);
  setTrainingFiltersDatepickerOpen(false);
  setTrainingTourSpotlightActive(false);
  resetTrainingFiltersTargetPadding();
  releaseAllShepherdTourFieldLocks();
  ensureShepherdModalOverlayHidden();
}

const TRAINING_FILTERS_STEP_OK = {
  text: 'OK',
  classes: 'shepherd-button-primary',
  action(this: ShepherdTour) {
    clearTrainingFiltersStepOverlay();
    dismissCurrentShepherdStep(this);
    queueMicrotask(() => tryShowTrainingResultsStepIfReady(this));
    requestAnimationFrame(() => tryShowTrainingResultsStepIfReady(this));
  },
};

export function buildTrainingBookTrainerTour(tour: ShepherdTour): void {
  registerInteractiveTourGuards(tour);
  registerTourHighlightedFieldLockGuards(tour, [TRAINING_FILTERS_STEP_ID], resolveTrainingFiltersTarget);

  const syncTrainingTourSpotlight = (stepId: string | undefined): void => {
    setTrainingTourSpotlightActive(!!stepId && TRAINING_SPOTLIGHT_STEP_IDS.has(stepId));
  };

  const syncTrainingSpotlightStepOverlay = (stepId: string | undefined): void => {
    if (!stepId || !TRAINING_SPOTLIGHT_STEP_IDS.has(stepId)) {
      return;
    }
    const step = tour.getById?.(stepId) as Parameters<typeof ensureShepherdModalOverlayVisibleForStep>[0];
    ensureShepherdModalOverlayVisibleForStep(step);
  };

  tour.on('complete', () => clearTrainingTourUi());
  tour.on('cancel', () => clearTrainingTourUi());

  tour.on('show', (ev: { step?: { id?: string } }) => {
    const stepId = ev?.step?.id;
    if (stepId === TRAINING_FILTERS_STEP_ID) {
      setTrainingTourSpotlightActive(false);
      ensureShepherdModalOverlayHidden();
      setTrainingFiltersInteractionActive(true);
      resetTrainingFiltersTargetPadding();
      return;
    }
    if (stepId) {
      setTrainingFiltersInteractionActive(false);
      setTrainingFiltersDatepickerOpen(false);
      resetTrainingFiltersTargetPadding();
      releaseAllShepherdTourFieldLocks();
      syncTrainingTourSpotlight(stepId);
      syncTrainingSpotlightStepOverlay(stepId);
      queueMicrotask(() => {
        syncTrainingTourSpotlight(stepId);
        syncTrainingSpotlightStepOverlay(stepId);
      });
      requestAnimationFrame(() => {
        syncTrainingTourSpotlight(stepId);
        syncTrainingSpotlightStepOverlay(stepId);
      });
    }
  });

  tour.on('hide', (ev: { step?: { id?: string } }) => {
    const stepId = ev?.step?.id;
    if (stepId === TRAINING_FILTERS_STEP_ID) {
      setTrainingFiltersInteractionActive(false);
      setTrainingFiltersDatepickerOpen(false);
      resetTrainingFiltersTargetPadding();
      return;
    }
    if (stepId) {
      setTrainingTourSpotlightActive(false);
    }
  });

  tour.addStep({
    id: TRAINING_FILTERS_STEP_ID,
    title: 'Tip',
    text: withTourFieldLockTipMessage(
      'Find golf instructors based on Golf Course, Area / City, and Date Range.',
    ),
    attachTo: { element: '.filters-section', on: 'bottom' },
    buttons: [TOUR_BUTTONS.skip, TRAINING_FILTERS_STEP_OK],
    ...SHEPHERD_TOUR_READONLY_HIGHLIGHT_STEP_OPTIONS,
    beforeShowPromise() {
      return waitForElement('.filters-section', 6000).then(() => undefined);
    },
  });

  tour.addStep({
    id: TRAINING_RESULTS_STEP_ID,
    title: 'Tip',
    text: 'Run a search. Trainers or an empty state will appear in this area.',
    attachTo: { element: '[data-tour="training-results-panel"]', on: 'top' },
    buttons: [TOUR_BUTTONS.skip, TRAINING_RESULTS_STEP_OK],
    showOn: () => trainingResultsPanelInDom(),
    modalOverlayOpeningPadding: 12,
    beforeShowPromise() {
      return waitForElement('[data-tour="training-results-panel"]', 120000).then(() => undefined);
    },
  });

  // When at least one bookable trainer row is shown.
  tour.addStep({
    id: TRAINING_LIST_BOOK_BTN_STEP_ID,
    title: 'Tip',
    text: 'After your search shows trainers, use Book training session to pick your date and time.',
    attachTo: { element: '[data-tour="training-book-session-btn"]', on: 'top' },
    buttons: [TOUR_BUTTONS.skip, listStep2Ok],
    showOn: () => !!document.querySelector('[data-tour="training-book-session-btn"]'),
    canClickTarget: true,
    beforeShowPromise() {
      return waitForElement('[data-tour="training-book-session-btn"]', 8000).then((el) => {
        if (el) {
          scrollTrainingBookSessionBtnIntoView();
        }
        return undefined;
      });
    },
  });

  // When search returns zero trainers — highlight empty state.
  tour.addStep({
    id: 'training-list-no-trainers',
    title: 'Tip',
    text: 'Improve your game by connecting with local instructors.',
    attachTo: { element: '[data-tour="training-no-trainers-empty"]', on: 'top' },
    buttons: [TOUR_BUTTONS.skip, TOUR_BUTTONS.finish],
    showOn: () => !!document.querySelector('[data-tour="training-no-trainers-empty"]'),
    beforeShowPromise() {
      return waitForElement('[data-tour="training-no-trainers-empty"]', 8000).then(() => undefined);
    },
  });
}
